import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Contract, ContractReplacement } from '../types';
import { agreementVariationNumber } from '../utils/agreementIdentity';

export type StageVariationState =
  'historical' | 'rejected' | 'failed' | 'active' | 'funding' | 'activating' | 'signing' | 'draft';

/** Where an ÄTA stands, as shown by the notice and the contract frame badge. */
export function stageVariationState(contract: Contract, settlement: ContractReplacement): StageVariationState {
  return contract.versionStatus === 'superseded'
    ? 'historical'
    : settlement.status === 'cancelled' ||
        contract.signingStatus === 'declined' ||
        contract.draftStatus === 'draft_rejected'
      ? 'rejected'
      : settlement.status === 'failed'
        ? 'failed'
        : settlement.status === 'completed'
          ? 'active'
          : settlement.status === 'awaiting_payment'
            ? 'funding'
            : settlement.status === 'signed' ||
                settlement.status === 'refund_pending'
              ? 'activating'
              : (settlement.contract ?? contract).companySigned
                ? 'signing'
                : 'draft';
}

/**
 * The ÄTA's state as text. An active ÄTA is named with every ÄTA before it,
 * since they all apply together with the contract; no single ÄTA is shown as
 * the one in force (ClickUp 869fgj5tr).
 */
function stageVariationText(t: TFunction, state: StageVariationState, contract: Contract, settlement: ContractReplacement) {
  const amendment = settlement.contract ?? contract;
  const number = agreementVariationNumber(amendment) ?? (amendment.contractVersion ?? 2) - 1;
  return t(state === 'active' && number === 1 ? 'stageVariation.state.activeFirst' : `stageVariation.state.${state}`, { number });
}

/** Compact badge for a settled ÄTA, placed in the contract frame's header
 * instead of the full notice. */
export function StageVariationBadge({ contract, settlement }: {
  contract: Contract;
  settlement: ContractReplacement;
}) {
  const { t } = useTranslation();
  return (
    <span className="contract-group-status-pill is-success" role="status" data-testid="stage-variation-notice">
      {stageVariationText(t, stageVariationState(contract, settlement), contract, settlement)}
    </span>
  );
}

export default function StageVariationNotice({
  contract,
  settlement,
  compact,
  onPay,
  onSign,
  isSigning
}: {
  contract: Contract;
  settlement: ContractReplacement;
  compact: boolean;
  onPay?: () => void;
  onSign?: () => void;
  isSigning: boolean;
}) {
  const { t } = useTranslation();
  const isAmendment = contract.id === settlement.replacementContractId;
  const state = stageVariationState(contract, settlement);
  const canSign =
    isAmendment &&
    onSign &&
    contract.companySigned &&
    !contract.personSigned &&
    !!contract.externalSigningProcessId &&
    !['declined', 'cancelled', 'expired', 'error'].includes(
      contract.signingStatus ?? ''
    ) &&
    state === 'signing';
  const format = (ore: number) =>
    new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(ore / 100);
  return (
    <div
      className={`alert alert-${state === 'failed' ? 'danger' : state === 'active' ? 'success' : 'warning'} py-2 mb-2`}
      role="status"
      data-testid="stage-variation-notice"
    >
      <strong>
        {stageVariationText(t, state, contract, settlement)}
      </strong>
      {!compact && (
        <>
          <p className="small mb-1 mt-1">{t('stageVariation.noticeHelp')}</p>
          <p className="small mb-1">
            {t('stageVariation.priceSummary', {
              gross: format(settlement.grossDeltaOre ?? 0),
              net: format(settlement.afterRotDeltaOre ?? 0)
            })}
          </p>
          {settlement.additionalPaymentOre > 0 && (
            <p className="small mb-1">
              {t('stageVariation.additionalFunding', {
                amount: format(settlement.additionalPaymentOre)
              })}
            </p>
          )}
          {settlement.refundAmountOre > 0 && (
            <p className="small mb-1">
              {t('stageVariation.refund', {
                amount: format(settlement.refundAmountOre)
              })}
            </p>
          )}
        </>
      )}
      {state === 'failed' && (
        <p className="small mb-1">{settlement.failureMessage}</p>
      )}
      {canSign && (
        <button
          type="button"
          className="btn btn-primary btn-sm mt-1"
          disabled={isSigning}
          onClick={onSign}
        >
          {t('contractReplacement.reviewAndSign')}
        </button>
      )}
      {isAmendment && state === 'funding' && onPay && (
        <button
          type="button"
          className="btn btn-warning btn-sm mt-1"
          onClick={onPay}
        >
          {t('contractReplacement.payDifference')}
        </button>
      )}
    </div>
  );
}
